import { Controller } from "@hotwired/stimulus";
import {
  AD_CONSTANTS,
  checkAdTiming,
  updateAdViewDate,
  isElementVisible,
  createAdObserver,
} from '../utils/adUtils'

/**
 * Controller for managing ad containers and their loading states
 */
export default class extends Controller {
  static classes = [
    "loadedWithAds",
    "loadedWithoutAds",
    "notLoaded",
    "removeWhenLoaded",
  ];

  static values = {
    adNameKey: String,
    freqInMinutes: Number,
    deferedAdNameKey: String,
    deferedTimeInSeconds: Number,
    waitBefore: Number, // wait to show in seconds
  };

  /** @type {MutationObserver} */
  #observer;

  connect() {
    this.#observer = createAdObserver(
      this.element,
      (mutationList, observer) => {
        if (
          this.element.querySelectorAll(`.${AD_CONSTANTS.ETHICAL_AD_CLASS}`)
            .length > 0
        ) {
          if (this.shouldLoadAds()) {
            this.handleAdsLoaded()
          }
          // Disconnect observer once we've handled the ad
          observer.disconnect()
        }
      },
    )
  }

  /**
   * Determine if ads should be loaded based on timing rules
   * @returns {boolean}
   */
  shouldLoadAds() {
    return (
      !this.hasAdNameKeyValue ||
      checkAdTiming(this.adNameKeyValue, this.freqInMinutesValue)
    )
  }

  /**
   * Handle the ad loading process including deferred loading logic
   */
  handleAdsLoaded() {
    const waitTime = (this.waitBeforeValue || 0) * AD_CONSTANTS.MS_PER_SECOND

    setTimeout(() => {
      try {
        const shouldShowNow =
          !this.hasDeferedAdNameKeyValue ||
          !this.hasDeferedTimeInSecondsValue ||
          checkAdTiming(
            this.deferedAdNameKeyValue,
            this.deferedTimeInSecondsValue / 60,
          )

        if (shouldShowNow) {
          this.showAd()
        } else {
          this.hideAd()
        }
      } catch (error) {
        console.error('Error handling ad load:', error)
        this.hideAd()
      }
    }, waitTime)
  }

  /**
   * Show the ad and update relevant states
   */
  showAd() {
    if (this.hasLoadedWithAdsClass) {
      this.element.classList.add(...this.loadedWithAdsClasses);
    }

    if (this.hasRemoveWhenLoadedClass) {
      this.element.classList.remove(...this.removeWhenLoadedClasses)
    }

    if (isElementVisible(this.element)) {
      this.updateLastViewedDate()
    }
  }

  /**
   * Hide the ad
   */
  hideAd() {
    if (this.hasLoadedWithoutAdsClass) {
      this.element.classList.add(...this.loadedWithoutAdsClasses);
    }
  }

  /**
   * Update the last viewed date in localStorage
   */
  updateLastViewedDate() {
    if (this.hasAdNameKeyValue) {
      updateAdViewDate(this.adNameKeyValue)
    }
  }

  /**
   * Clean up when the controller is disconnected
   */
  disconnect() {
    if (this.#observer) {
      this.#observer.disconnect();
    }
  }

  /**
   * Collapse the ad (for popup and takeover ads)
   */
  collapse() {
    this.element.classList.add(AD_CONSTANTS.HIDDEN_CLASS);
  }
}
